import { useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import {
  getDataTableGridConfig,
  type TGetTableWidgetConfig,
  flattenTreeData,
} from './utils';
import type {
  TModuleDisplayOption,
  TWidget,
  TWidgetData,
} from 'utility/types/dataProvider';

type TTableWidgetResults = TWidgetData['results'];
type TUseTableWidgetConfigParams = {
  widget: TWidget;
  displayConfig: TModuleDisplayOption | undefined;
  data: TWidgetData;
  pagination: any;
  options: any;
};

/**
 * Normalizes data results to ensure each item has an id and title property.
 */
export const normalizeDataResults = (
  results: TTableWidgetResults | undefined,
  identifier: string,
  isTree: boolean = false,
  treeDataKey: string | null = null,
): TTableWidgetResults => {
  if (!results || !Array.isArray(results)) return [];

  // For tree data, flatten the nested structure
  if (isTree && treeDataKey) {
    try {
      const flattenedRows = flattenTreeData(results, treeDataKey, identifier);

      // Ensure each row has an id and title
      return flattenedRows.map((row: any) => ({
        ...row,
        id: row.id ?? v4(),
        title: row.title || row[identifier] || 'Unknown',
      }));
    } catch (error) {
      console.error('Error flattening tree data:', error);
      // Fall back to standard normalization if tree flattening fails
    }
  }

  // Standard normalization for non-tree data
  return results.map((row: any) => ({
    ...row,
    id: row?.id ?? row?.[identifier] ?? v4(),
    title: row?.title || 'Unknown',
  }));
};

export const useTableWidgetConfig = ({
  widget,
  displayConfig,
  data,
  pagination,
  options,
}: TUseTableWidgetConfigParams) => {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >({});

  // Generate config with normalized rows
  const config = useMemo<TGetTableWidgetConfig & { rows: any[] }>(() => {
    const {
      columns = [],
      icons = undefined,
      legend = undefined,
      initHiddenColumns = {},
      isTree = false,
      treeDataKey = null,
      groupingField = null,
    } = getDataTableGridConfig(widget);

    // Use a safe identifier - prefer displayConfig.identifier, fall back to 'id'
    // For tree data, use the grouping field as the identifier
    const identifier =
      isTree && groupingField ? groupingField.field : displayConfig?.identifier || 'id';

    const rows = normalizeDataResults(data?.results, identifier, isTree, treeDataKey);

    return {
      columns,
      icons,
      legend,
      initHiddenColumns,
      isTree,
      treeDataKey,
      groupingField,
      rows,
    };
  }, [data?.results, displayConfig?.identifier, widget]);

  // Initialize column visibility from config
  useEffect(() => {
    if (config?.initHiddenColumns) {
      setColumnVisibilityModel(config.initHiddenColumns);
    }
  }, [config.initHiddenColumns]);

  // Pagination calculations
  const hasPagination = !!pagination;
  const currentPage = hasPagination
    ? pagination.page - 1
    : (options.currentPage ?? 1) - 1;
  const currentPageSize = hasPagination ? pagination.pageSize : (options.pageSize ?? 10);

  const paginatedRows = useMemo(() => {
    // For tree data, never slice the rows - MUI DataGrid needs all rows to render the tree structure.
    if (config.isTree) return config.rows;

    if (!hasPagination) return config.rows;
    if ((config?.rows?.length ?? 0) > currentPageSize) {
      const start = currentPage * currentPageSize;
      return config.rows.slice(start, start + currentPageSize);
    }
    return config.rows;
  }, [config.rows, config.isTree, currentPage, currentPageSize, hasPagination]);

  const totalRowCount = hasPagination
    ? Math.max(pagination?.total ?? 0, paginatedRows.length)
    : (config?.rows?.length ?? Number(options?.rowCount ?? 0));

  const pageSizeOptions = hasPagination
    ? Array.from(new Set([pagination.pageSize, 5, 10, 25])).sort((a, b) => a - b)
    : [options.pageSize];

  return {
    config,
    columnVisibilityModel,
    setColumnVisibilityModel,
    paginatedRows,
    currentPage,
    currentPageSize,
    totalRowCount,
    pageSizeOptions,
    hasPagination,
  };
};
